import { useCallback, useEffect, useRef } from 'react';
import { useFragment, graphql } from 'react-relay';

import { getUserSessionRegions } from 'dibs-regional-info/exports/regionalInfoHelpers';

import { useSearchCorrections } from '../../useSearchCorrections';
import {
    trackEcommerceProductClick,
    trackEcommerceProductImpressions,
    type ItemData,
    type UrgencySignal,
} from '../../../utils/tracking/searchBrowse/ecommerceTracking';
import { useSbSelector } from '../../../reducers/useSbSelector';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';

import {
    type ItemShippingPrices,
    type GetItemShippingTrackingData,
} from '../useItemShippingPrices';

import { type useSbSharedItemTracking_item$key } from './__generated__/useSbSharedItemTracking_item.graphql';
import { type useSbSharedItemTracking_itemSearch$key } from './__generated__/useSbSharedItemTracking_itemSearch.graphql';
import { type useSbSharedItemTracking_viewer$key } from './__generated__/useSbSharedItemTracking_viewer.graphql';

const viewerFragment = graphql`
    fragment useSbSharedItemTracking_viewer on Viewer {
        regionalInfo(zipCode: $userZipCode, countryCode: $userCountryCode)
            @include(if: $fetchRegionalInfo) {
            regionsByZipCode {
                displayName
            }
        }
    }
`;

const itemSearchFragment = graphql`
    fragment useSbSharedItemTracking_itemSearch on ItemSearchQueryConnection {
        ...ecommerceTracking_itemSearch
        ...useSearchCorrections_itemSearch
    }
`;

const itemFragment = graphql`
    fragment useSbSharedItemTracking_item on Item @relay(plural: true) {
        serviceId
        ...ecommerceTracking_item
    }
`;

type TrackingFunctionArgs = {
    itemId: string | null;
    index: number;
    isSponsored?: boolean;
};

export type TrackingFunction = (props: TrackingFunctionArgs) => void;

type Item = Omit<ItemData, 'urgencySignal' | 'shippingPrices' | 'shippingQuote'>;
type Args = {
    viewer: useSbSharedItemTracking_viewer$key | null | undefined;
    itemSearch: useSbSharedItemTracking_itemSearch$key | null | undefined;
    items: useSbSharedItemTracking_item$key | null | undefined;
    pageType?: string;
    getUrgencySignal?: (itemId: string | null | undefined) => UrgencySignal;
    getItemShippingPrices?: (itemId: string | null | undefined) => ItemShippingPrices | null;
    getItemShippingTrackingData?: GetItemShippingTrackingData;
};

const dedupeItem = ({ itemId, index }: { itemId: string; index: number }): string =>
    `${index}_${itemId}`;

const MAX_ITEMS_PER_BATCH = 20;
const BATCH_DELAY_MS = 200;

export const useSbSharedItemTracking = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    items: itemsRef,
    pageType,
    getUrgencySignal,
    getItemShippingPrices,
    getItemShippingTrackingData,
}: Args): {
    fireItemClickTracking: TrackingFunction;
    fireItemImpressionTracking: TrackingFunction;
} => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const items = useFragment(itemFragment, itemsRef);
    const [searchCorrectionTerm] = useSearchCorrections({ itemSearch });

    const { isDesktop } = useServerVarsContext();
    const pageSize = useSbSelector(state => state.relayVariables.variables.first) || 60;
    const page = useSbSelector(state => state.filters.page);

    const itemImpressionQueueRef = useRef<Item[]>([]);
    const trackedImpressionsRef = useRef<string[]>([]);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const getRegions = useCallback(() => {
        return getUserSessionRegions() || viewer?.regionalInfo?.[0]?.regionsByZipCode || [];
    }, [viewer]);

    const trackImpressions = useCallback(
        (impressionItems: Item[]) => {
            const userRegions = getRegions();
            const itemsData = impressionItems.map(impressionItem => {
                const urgencySignal = getUrgencySignal?.(impressionItem.itemId) || null;
                const shippingPrices = getItemShippingPrices?.(impressionItem.itemId) || null;
                const shippingQuote = getItemShippingTrackingData?.(impressionItem.itemId) || null;

                return {
                    ...impressionItem,
                    urgencySignal,
                    shippingPrices,
                    shippingQuote,
                };
            }, [] as ItemData[]);

            trackEcommerceProductImpressions({
                items,
                itemSearch,
                itemsData,
                searchCorrectionTerm,
                userRegions,
                pageType,
                page,
                pageSize,
                isDesktop,
            });
        },
        [
            getRegions,
            items,
            itemSearch,
            getUrgencySignal,
            getItemShippingPrices,
            getItemShippingTrackingData,
            searchCorrectionTerm,
            pageType,
            page,
            pageSize,
            isDesktop,
        ]
    );

    const processItemImpressionQueue = useCallback(() => {
        if (itemImpressionQueueRef.current.length > 0) {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }

            trackImpressions(itemImpressionQueueRef.current);
            itemImpressionQueueRef.current = [];
        }
    }, [trackImpressions]);

    // Process the queue when component unmounts
    useEffect(() => {
        return () => {
            processItemImpressionQueue();
        };
    }, [processItemImpressionQueue]);

    // Reset the tracked impressions when the items change, e.g on page or filters change
    // Use primitive itemIdsAsString to avoid unnecessary resets
    const itemIdsAsString = items?.map(item => item.serviceId).join(',') || '';
    useEffect(() => {
        trackedImpressionsRef.current = [];
    }, [itemIdsAsString]);

    const fireItemImpressionTracking: TrackingFunction = useCallback(
        ({ itemId, index, isSponsored }) => {
            if (itemId) {
                if (trackedImpressionsRef.current.includes(dedupeItem({ itemId, index }))) {
                    return;
                } else {
                    trackedImpressionsRef.current.push(dedupeItem({ itemId, index }));
                }

                itemImpressionQueueRef.current.push({ itemId, index, isSponsored });

                // Start timeout if this is the first item
                if (itemImpressionQueueRef.current.length === 1 && !timeoutRef.current) {
                    timeoutRef.current = setTimeout(() => {
                        processItemImpressionQueue();
                    }, BATCH_DELAY_MS);
                }

                // Process immediately if we hit the batch size limit
                if (itemImpressionQueueRef.current.length >= MAX_ITEMS_PER_BATCH) {
                    processItemImpressionQueue();
                }
            }
        },
        [processItemImpressionQueue]
    );

    const fireItemClickTracking: TrackingFunction = useCallback(
        ({ itemId, index, isSponsored }) => {
            if (itemId) {
                const userRegions = getRegions();
                // In case clicking on item while impression tracking haven't been done
                fireItemImpressionTracking({
                    itemId,
                    index,
                    isSponsored,
                });

                trackEcommerceProductClick({
                    itemSearch,
                    items,
                    index,
                    searchCorrectionTerm,
                    isSponsored,
                    pageType,
                    userRegions,
                    page,
                    pageSize,
                    isDesktop,
                    urgencySignal: getUrgencySignal?.(itemId) || null,
                    shippingPrices: getItemShippingPrices?.(itemId) || null,
                    shippingQuote: getItemShippingTrackingData?.(itemId) || null,
                });
            }
        },
        [
            getRegions,
            itemSearch,
            items,
            getUrgencySignal,
            getItemShippingPrices,
            getItemShippingTrackingData,
            searchCorrectionTerm,
            pageType,
            page,
            pageSize,
            isDesktop,
            fireItemImpressionTracking,
        ]
    );

    return {
        fireItemClickTracking,
        fireItemImpressionTracking,
    };
};
